import { useAchievement } from "../../context/AchievementContext";
import { useState, useEffect, useRef, useCallback } from "react";
import "./Achievements.css";

/**
 * achievements display component - shows stacked notifications at bottom right
 */
export default function Achievements() {
  const { achievements, removeAchievement } = useAchievement();
  const [animatingOut, setAnimatingOut] = useState<Set<string>>(new Set());
  const timeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const currentAchievementsRef = useRef<Set<string>>(new Set());

  const handleRemove = useCallback(
    (displayId: string) => {
      setAnimatingOut((prev) => new Set([...prev, displayId]));

      // remove from DOM after animation completes
      setTimeout(() => {
        removeAchievement(displayId);
        setAnimatingOut((prev) => {
          const next = new Set(prev);
          next.delete(displayId);
          return next;
        });
        // clean up timeout ref
        timeoutsRef.current.delete(displayId);
      }, 300); // matches animation duration
    },
    [removeAchievement],
  );

  // set up auto-timeout for each new achievement
  useEffect(() => {
    // find new achievements not yet in currentAchievementsRef
    achievements.forEach((achievement) => {
      if (currentAchievementsRef.current.has(achievement.displayId)) {
        return; // already processed
      }

      // mark as processed
      currentAchievementsRef.current.add(achievement.displayId);

      const timeout = achievement.timeout ?? 5000;
      const timeoutId = setTimeout(() => {
        handleRemove(achievement.displayId);
      }, timeout);

      timeoutsRef.current.set(achievement.displayId, timeoutId);
    });

    // clean up timeouts for achievements that no longer exist
    const existingIds = new Set(achievements.map((a) => a.displayId));
    for (const displayId of currentAchievementsRef.current) {
      if (!existingIds.has(displayId)) {
        const timeoutId = timeoutsRef.current.get(displayId);
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutsRef.current.delete(displayId);
        }
        currentAchievementsRef.current.delete(displayId);
      }
    }
  }, [achievements, handleRemove]);

  return (
    <div className="achievements-container">
      {achievements.map((achievement, index) => (
        <div
          key={achievement.displayId}
          className={`achievement-notification ${animatingOut.has(achievement.displayId) ? "slideOut" : ""}`}
          style={{ bottom: `calc(28px + ${index * 80}px)` }}
          onClick={() => handleRemove(achievement.displayId)}
        >
          <div
            className="achievement-icon"
            style={{ backgroundColor: achievement.color || "#87ceeb" }}
          >
            {achievement.icon || <span className="achievement-default-icon">★</span>}
          </div>

          <div className="achievement-content">
            <h3 className="achievement-title">{achievement.title}</h3>
            <p className="achievement-description">{achievement.description}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
